import apiClient from "@/api/client";
import type { CompanyHoliday, EmployeeDayOff, OwedDayOff, ScheduleData, WorkScheduleEntry } from "../types";

export const scheduleApi = {
  getSchedule: (start?: string, end?: string, includeInactive?: boolean) =>
    apiClient
      .get<{ data: ScheduleData }>("/schedule/", {
        params: {
          ...(start ? { start, end } : {}),
          ...(includeInactive ? { include_inactive: "1" } : {}),
        },
      })
      .then((r) => r.data.data),

  createEntry: (data: { entry_type: string; start_date: string; end_date: string; note?: string }) =>
    apiClient
      .post<{ data: WorkScheduleEntry }>("/schedule/entries/", data)
      .then((r) => r.data.data),

  deleteEntry: (id: string) => apiClient.delete(`/schedule/entries/${id}/`),

  approveEntry: (id: string, comment?: string) =>
    apiClient
      .post<{ data: WorkScheduleEntry }>(`/schedule/entries/${id}/approve/`, { comment: comment ?? "" })
      .then((r) => r.data.data),

  rejectEntry: (id: string, comment: string) =>
    apiClient
      .post<{ data: WorkScheduleEntry }>(`/schedule/entries/${id}/reject/`, { comment })
      .then((r) => r.data.data),

  listEntries: (params?: Record<string, string>) =>
    apiClient
      .get<{ data: WorkScheduleEntry[] }>("/schedule/entries/", { params })
      .then((r) => r.data.data),

  setEntryHours: (id: string, payload: { start_hour?: string | null; hours_ranges?: Array<{ start: string; end: string }> | null }) =>
    apiClient
      .patch<{ data: WorkScheduleEntry }>(`/schedule/entries/${id}/`, payload)
      .then((r) => r.data.data),

  listCompanyHolidays: () =>
    apiClient.get<{ data: CompanyHoliday[] }>("/company-holidays/").then((r) => r.data.data),

  createCompanyHoliday: (data: { start_date: string; end_date: string; description?: string; compensates_for?: string | null }) =>
    apiClient.post<{ data: CompanyHoliday }>("/company-holidays/", data).then((r) => r.data.data),

  deleteCompanyHoliday: (id: string) => apiClient.delete(`/company-holidays/${id}/`),

  listEmployeeDaysOff: (employee: string) =>
    apiClient
      .get<{ data: EmployeeDayOff[]; meta: { count: number; first_work_day?: string | null } }>(
        "/employee-days-off/",
        { params: { employee } },
      )
      .then((r) => ({ entries: r.data.data, firstWorkDay: r.data.meta.first_work_day ?? null })),

  createEmployeeDayOff: (data: { employee: string; date: string; compensates_for: string; description?: string }) =>
    apiClient.post<{ data: EmployeeDayOff }>("/employee-days-off/", data).then((r) => r.data.data),

  deleteEmployeeDayOff: (id: string) => apiClient.delete(`/employee-days-off/${id}/`),

  /** Without `employee`: every owed employee in the requester's scope. */
  listOwedDaysOff: (employee?: string) =>
    apiClient
      .get<{ data: OwedDayOff[] }>("/employee-days-off/owed/", { params: employee ? { employee } : {} })
      .then((r) => r.data.data),
};
